import React from "react";
import {
	Box,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	Typography,
	useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
	MISSING_SIGNATURE_REASONS,
	MissingSignatureReason,
	applyExclusiveSelection,
	isMissingSignatureReason,
	resolveDefaultSignatureText,
} from "./missingSignatureReason";

const NS = "raCheckOutIn.signature.missingSignature";

export interface MissingSignatureReasonSelectProps {
	/** Motivazione selezionata (esclusiva) o null se nessuna. */
	value: MissingSignatureReason | null;
	/** Callback con la nuova motivazione (o null se deselezionata). */
	onChange: (reason: MissingSignatureReason | null) => void;
	/** Lingua del RA per la traduzione del testo di default (IT/FR/ES/PT/EN). */
	language?: string;
}

/**
 * SCFE-1 — Selezione ESCLUSIVA (VB-06) della motivazione di mancata firma nel ramo
 * Network check-in. Usa un RadioGroup (esclusività nativa) e mostra l'anteprima del
 * testo di default tradotto per lingua che verrà riportato sulla copia di check-in.
 * Fallback sul code grezzo se la traduzione è assente.
 */
const MissingSignatureReasonSelect = ({
	value,
	onChange,
	language,
}: MissingSignatureReasonSelectProps) => {
	const theme = useTheme();
	const { t } = useTranslation(["network"]);

	const lookup = (code: string): string | null => {
		const key = `${NS}.defaultText.${code}`;
		const translated = t(key, { lng: language, defaultValue: "" }) as string;
		return translated === "" ? null : translated;
	};

	const handleChange = (_: React.ChangeEvent<HTMLInputElement>, raw: string) => {
		if (!isMissingSignatureReason(raw)) {
			return;
		}
		onChange(applyExclusiveSelection(value, raw));
	};

	const previewText = resolveDefaultSignatureText(value, lookup);

	return (
		<FormControl component="fieldset" fullWidth data-testid="missing-signature-reason">
			<FormLabel component="legend">{t(`${NS}.label.title`)}</FormLabel>
			<RadioGroup
				aria-label="missing-signature-reason"
				name="missing-signature-reason"
				value={value ?? ""}
				onChange={handleChange}
			>
				{MISSING_SIGNATURE_REASONS.map((reason) => (
					<FormControlLabel
						key={reason}
						value={reason}
						control={
							<Radio
								inputProps={{ "aria-label": reason } as any}
								onClick={() => {
									// RadioGroup non emette onChange sul re-click dell'opzione già
									// selezionata: gestiamo qui il toggle-off (deselezione).
									if (value === reason) {
										onChange(null);
									}
								}}
							/>
						}
						label={t(`${NS}.reason.${reason}`)}
						data-testid={`missing-signature-reason-option-${reason}`}
					/>
				))}
			</RadioGroup>

			{!!previewText && (
				<Box sx={{ mt: theme.spacing(1) }}>
					<Typography variant="bodySmall" color="text.secondary">
						{t(`${NS}.label.preview`)}
					</Typography>
					<Typography variant="body" data-testid="missing-signature-reason-preview">
						{previewText}
					</Typography>
				</Box>
			)}
		</FormControl>
	);
};

export default MissingSignatureReasonSelect;
